import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Container from '../../shared/Container';
import { CTA_FORMULARIO_HREF, CTA_FORMULARIO_LABEL } from '../../../data/cataIncendios';
import styles from './Banner.module.css';

export interface Notice {
  id: string;
  icon?: ReactNode;
  date: string;
  day?: string | number;
  title: string;
  description: string;
  /** Acción principal. `href` externo abre en pestaña nueva. */
  buttonText: string;
  buttonHref: string;
  /** Acción secundaria opcional, normalmente una ruta interna de ampliación. */
  secondaryText?: string;
  secondaryTo?: string;
  type?: 'fire' | 'alert' | 'info';
}

export interface BannerProps {
  notices?: Notice[];
  title?: string;
  subtitle?: string;
  onClose?: () => void;
}

/* Iconos copiados tal cual de `components/blocks/Icon/Icon.tsx` del Portal de
   Pacientes, para que el bloque se vea idéntico. `fire` no es un icono de trazo
   sino una ilustración a color en un viewBox de 64x64: por eso el original lo
   pinta a 144px y los de trazo a 56px. */

function FireIcon({ size = 144 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true">
      <rect x="8" y="42" width="48" height="14" rx="3" fill="#8D5A44" />
      <rect x="8" y="52" width="48" height="4" rx="2" fill="#6F4637" />

      <path
        d="M33 54c0-6 8-7 8-13 0-4-2-6-5-8 1 5-4 6-4 11 0 3 2 5 4 7-4 0-8-3-8-8 0-8 6-12 8-18 2 3 7 6 7 13 0 8-5 12-10 16z"
        fill="#FF6B4A"
      />
      <path
        d="M28 54c0-4 5-5 5-9 0-3-1-4-3-6 0 3-3 4-3 7 0 2 1 3 2 5-3 0-5-2-5-5 0-6 4-9 6-13 1 2 5 4 5 10 0 6-4 8-7 11z"
        fill="#FF3B30"
      />

      <rect x="14" y="22" width="2.5" height="24" rx="1" fill="#6B4B3D" />
      <path d="M15 25l5-5M15 30l4-4" stroke="#6B4B3D" strokeWidth="2" strokeLinecap="round" />

      <path d="M26 18l6-8 6 8z" fill="#F7A623" />
      <path d="M24 24l8-10 8 10z" fill="#FFC233" />
      <path d="M23 30l9-10 9 10z" fill="#FF8A2A" />
      <rect x="30.5" y="30" width="3" height="12" rx="1.5" fill="#7C523F" />

      <path d="M38 17l5-7 5 7z" fill="#F7A623" />
      <path d="M36 23l7-9 7 9z" fill="#FFC233" />
      <path d="M35 29l8-9 8 9z" fill="#FF8A2A" />
      <rect x="41.5" y="29" width="3" height="13" rx="1.5" fill="#7C523F" />

      <circle cx="10" cy="12" r="1.2" fill="#FF6B4A" />
      <circle cx="20" cy="8" r="1" fill="#FF8A2A" />
      <circle cx="33" cy="7" r="1" fill="#FF3B30" />
      <circle cx="47" cy="10" r="1.2" fill="#FF6B4A" />
    </svg>
  );
}

const PATHS = {
  bell: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0',
  'arrow-right': 'M5 12h14M12 5l7 7-7 7',
  x: 'M18 6 6 18M6 6l12 12',
  'alert-triangle':
    'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01',
  info: 'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM12 8h.01M12 12v4',
} as const;

function StrokeIcon({ name, size = 24 }: { name: keyof typeof PATHS; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentcolor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

function glyphPorTipo(type: Notice['type']) {
  if (type === 'info') return <StrokeIcon name="info" size={56} />;
  if (type === 'alert') return <StrokeIcon name="alert-triangle" size={56} />;
  return <FireIcon size={144} />;
}

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

/**
 * Fecha del aviso en el formato que espera la tarjeta: "26 AGO 2026".
 *
 * Se compone a mano en vez de con `toLocaleDateString`: el ICU devuelve el mes
 * en minusculas y con punto en algunas versiones ("ago." frente a "ago"), y el
 * bloque de fecha parte la cadena por espacios para separar dia y mes.
 */
function fechaDeHoy(): string {
  const hoy = new Date();

  return `${hoy.getDate()} ${MESES[hoy.getMonth()]} ${hoy.getFullYear()}`;
}

function avisosPorDefecto(): Notice[] {
  return [
    {
      id: '1',
      date: fechaDeHoy(),
      title: 'Solicitud de Prestación por Cese de Actividad Fuerza Mayor Incendio',
      description: 'Ya tiene a su disposición el nuevo formulario para solicitar esta nueva prestación.',
      buttonText: CTA_FORMULARIO_LABEL,
      buttonHref: CTA_FORMULARIO_HREF,
      secondaryText: 'Más información',
      secondaryTo: '/prestacion-cata-incendios',
      type: 'fire',
    },
  ];
}

export default function Banner({
  notices,
  title = 'Avisos importantes',
  subtitle = 'Avisos que requieren tu atención. No los pierdas de vista.',
  onClose,
}: BannerProps) {
  // Se resuelve en el render, no al importar el modulo: asi la fecha sigue
  // siendo la de hoy aunque la pestana lleve abierta desde ayer.
  const avisos = notices ?? avisosPorDefecto();

  if (avisos.length === 0) {
    return null;
  }

  const [principal] = avisos;

  if (!principal) {
    return null;
  }

  // "26 AGO 2026" -> día "26" y resto "AGO 2026", como en el bloque original.
  const partes = principal.date.split(' ');
  const dia = principal.day ?? partes[0] ?? '';
  const mes = partes.slice(1).join(' ');

  const esInfo = principal.type === 'info';

  return (
    <section className={styles.section} aria-labelledby="avisos-importantes">
      <Container>
        <div className={styles.head}>
          <span className={styles.badge}>
            <StrokeIcon name="bell" size={16} />
            <span>{avisos.length}</span>
          </span>
          <div>
            <h2 id="avisos-importantes" className={styles.title}>
              {title}
            </h2>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
        </div>

        <article
          className={`${styles.feature} ${esInfo ? styles.featureInfo : styles.featureWarn}`}
          aria-label="Aviso principal"
        >
          {onClose && (
            <button type="button" className={styles.close} onClick={onClose} aria-label="Cerrar aviso">
              <StrokeIcon name="x" size={16} />
            </button>
          )}

          <aside className={styles.side} aria-hidden="true">
            <div className={styles.glyph}>{principal.icon ?? glyphPorTipo(principal.type)}</div>
            <div className={styles.date}>
              <div className={styles.dateDay}>{dia}</div>
              <div className={styles.dateMonth}>{mes}</div>
            </div>
          </aside>

          <div className={styles.main}>
            <h3 className={styles.featureTitle}>{principal.title}</h3>
            <p className={styles.body}>{principal.description}</p>
            <div className={styles.actions}>
              <a
                className={styles.cta}
                href={principal.buttonHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {principal.buttonText}
                <StrokeIcon name="arrow-right" size={16} />
              </a>
              {principal.secondaryText && principal.secondaryTo && (
                <Link className={styles.ctaSecondary} to={principal.secondaryTo}>
                  {principal.secondaryText}
                  <StrokeIcon name="arrow-right" size={16} />
                </Link>
              )}
            </div>
          </div>
        </article>
      </Container>
    </section>
  );
}
